import CustomSheet from "@/components/shared/Sheet/Sheet";
import { Button } from "@/components/ui/button";
import LayoutInside from "@/layout/LayoutInside";
import useModalStore from "@/store/useModalStore";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FILTER_MODAL_KEY, useAnalyticsPanel } from "./hooks/useAnalyticsPanel";
import { AnalyticsToolbar } from "./Partials/AnalyticsToolbar";
import { CheckoutMetricsTable } from "./Partials/CheckoutMetricsTable";
import FilterAnalytics from "./Partials/FilterAnalytics";
import { FunnelCharts } from "./Partials/FunnelCharts";
import { OverviewCards } from "./Partials/OverviewCards";
import { PaymentMethodCards } from "./Partials/PaymentMethodCards";
import { ProductSalesCard } from "./Partials/ProductSalesCard";
import { SalesByCountryCard } from "./Partials/SalesByCountryCard";
import { SalesByCurrencyCard } from "./Partials/SalesByCurrencyCard";
import { SalesByHourChart } from "./Partials/SalesByHourChart";
import { SalesByPeriodCharts } from "./Partials/SalesByPeriodCharts";
import { TemplateCards } from "./Partials/TemplateCards";

export default function Analytics() {
  const { t } = useTranslation("common");
  const { modals, openModal, closeModal } = useModalStore();
  const panel = useAnalyticsPanel();

  const charts = panel.analytics?.charts;
  const isLoading = panel.isLoadingAnalytics;

  return (
    <LayoutInside items={[{ text: "Analytics", link: "/reports/analytics" }]}>
      <CustomSheet
        open={modals[FILTER_MODAL_KEY]}
        onClose={() => closeModal(FILTER_MODAL_KEY)}
      >
        <FilterAnalytics
          filterValues={panel.filterValues}
          onFilter={panel.applyFilterForm}
        />
      </CustomSheet>

      <div className="grid gap-6">
        <Link to="/reports" className="w-fit">
          <Button
            variant="default"
            className="bg-transparent text-colorText hover:bg-transparent p-0"
          >
            <ArrowLeft size={16} />
            {t("common:back")}
          </Button>
        </Link>

        <AnalyticsToolbar
          dateRange={panel.dateRange}
          onDateRangeChange={panel.handleDateRangeChange}
          onOpenFilters={() => openModal(FILTER_MODAL_KEY)}
          hasActiveFilters={panel.hasActiveFilters}
          filterBadges={panel.filterBadges}
          onClearFilters={panel.clearAllFilters}
        />

        <OverviewCards overview={panel.analytics?.overview} isLoading={isLoading} />
        <FunnelCharts funnels={panel.analytics?.funnels} isLoading={isLoading} />
        <SalesByHourChart salesByHour={charts?.sales_by_hour} isLoading={isLoading} />
        <SalesByPeriodCharts
          salesByDayOfWeek={charts?.sales_by_day_of_week}
          salesByMonth={charts?.sales_by_month}
          isLoading={isLoading}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <SalesByCountryCard salesByCountry={charts?.sales_by_country} isLoading={isLoading} />
          <SalesByCurrencyCard salesByCurrency={charts?.sales_by_currency} isLoading={isLoading} />
        </div>

        <PaymentMethodCards
          salesByPaymentMethod={charts?.sales_by_payment_method}
          isLoading={isLoading}
        />
        <TemplateCards salesByTemplate={charts?.sales_by_template} isLoading={isLoading} />
        <ProductSalesCard salesByProduct={charts?.sales_by_product} isLoading={isLoading} />

        <CheckoutMetricsTable
          data={panel.checkouts}
          isLoading={
            isLoading ||
            panel.isLoadingCheckouts ||
            panel.isFetchingAnalytics ||
            panel.isFetchingCheckouts
          }
          onPageChange={panel.setCheckoutsPage}
        />
      </div>
    </LayoutInside>
  );
}
